import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useGoogleMapsLoader } from '../hooks/useGoogleMapsLoader';
import { Autocomplete } from '@react-google-maps/api';
import { getEnv } from '../config';
import axios from 'axios';

const API_URL = getEnv('REACT_APP_API_URL') || '';

// 12 photos for the background grid (subset of 40 available)
const PHOTOS = [
    'DSC08867.JPG', 'DSC07053.JPG', 'DSC09159.JPG', 'DSC06358.JPG',
    'DSC09978.JPG', 'DSC08367.JPG', 'DSC06717.JPG', 'DSC08195.JPG',
    'DSC09583.JPG', 'DSC06771.JPG', 'DSC08747.JPG', 'DSC09139.JPG',
];

// All 40 photos for random swapping
const ALL_PHOTOS = [
    'DSC05699.JPG','DSC05996.JPG','DSC06003.JPG','DSC06358.JPG','DSC06717.JPG',
    'DSC06735.JPG','DSC06771.JPG','DSC06936.JPG','DSC06938.JPG','DSC06944.JPG',
    'DSC06987.JPG','DSC07014.JPG','DSC07053.JPG','DSC07059.JPG','DSC08176.JPG',
    'DSC08195.JPG','DSC08204.JPG','DSC08232.JPG','DSC08367.JPG','DSC08455.JPG',
    'DSC08491.JPG','DSC08593.JPG','DSC08710.JPG','DSC08737.JPG','DSC08747.JPG',
    'DSC08763.JPG','DSC08785.JPG','DSC08793.JPG','DSC08854.JPG','DSC08867.JPG',
    'DSC09119.JPG','DSC09139.JPG','DSC09159.JPG','DSC09577.JPG','DSC09583.JPG',
    'DSC09604.JPG','DSC09627.JPG','DSC09631.JPG','DSC09687.JPG','DSC09978.JPG',
];

function PhotoCell({ index }) {
    const baseOpacity = useRef(0.3 + Math.random() * 0.4);
    const [layers, setLayers] = useState(() => [
        { src: PHOTOS[index % PHOTOS.length], opacity: baseOpacity.current, key: 0 },
    ]);
    const keyRef = useRef(1);

    useEffect(() => {
        // Stagger start so cells don't all swap at once
        const delay = 4000 + Math.random() * 16000;
        const interval = 10000 + Math.random() * 20000;

        const timeout = setTimeout(() => {
            const swap = () => {
                const newPhoto = ALL_PHOTOS[Math.floor(Math.random() * ALL_PHOTOS.length)];
                const newOpacity = 0.3 + Math.random() * 0.4;
                const newKey = keyRef.current++;
                setLayers((prev) => {
                    // Keep only the most recent layer + add new one on top (fading in)
                    const top = prev[prev.length - 1];
                    return [top, { src: newPhoto, opacity: newOpacity, key: newKey }];
                });
            };
            swap();
            const id = setInterval(swap, interval);
            return () => clearInterval(id);
        }, delay);

        return () => clearTimeout(timeout);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="relative overflow-hidden">
            {layers.map((layer, i) => (
                <img
                    key={layer.key}
                    src={`/photos/${layer.src}`}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{
                        opacity: i === layers.length - 1 ? layer.opacity : 0,
                        transition: 'opacity 2s ease-in-out',
                    }}
                    loading="lazy"
                />
            ))}
        </div>
    );
}

function PhotoGrid() {
    return (
        <div className="absolute inset-0 grid grid-cols-3 sm:grid-cols-4 grid-rows-4 sm:grid-rows-3 gap-0 overflow-hidden">
            {PHOTOS.map((_, i) => (
                <PhotoCell key={i} index={i} />
            ))}
        </div>
    );
}

function formatPhone(raw) {
    if (!raw) return '';
    const digits = raw.replace(/\D/g, '');
    if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    if (digits.length === 11 && digits[0] === '1') {
        return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    return raw;
}

function LeaderCard({ leader }) {
    const photoUrl = leader.photo_url
        ? `${API_URL}/api/photos/${leader.photo_url}`
        : null;

    const initials = `${(leader.first_name || '')[0] || ''}${(leader.last_name || '')[0] || ''}`.toUpperCase();
    const digits = (leader.phone || '').replace(/\D/g, '');
    const smsHref = digits ? `sms:${digits}` : null;

    return (
        <a
            href={smsHref || '#'}
            className="flex items-center gap-4 p-4 bg-white/10 backdrop-blur-md rounded-xl border border-white/20 hover:bg-white/20 transition-colors cursor-pointer"
            onClick={smsHref ? undefined : (e) => e.preventDefault()}
        >
            {photoUrl ? (
                <img
                    src={photoUrl}
                    alt={`${leader.first_name} ${leader.last_name}`}
                    className="w-16 h-16 rounded-full object-cover ring-2 ring-white/30 flex-shrink-0"
                />
            ) : (
                <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center text-white text-xl font-bold ring-2 ring-white/30 flex-shrink-0">
                    {initials}
                </div>
            )}
            <div className="min-w-0">
                <p className="text-white font-semibold text-lg truncate">
                    {leader.first_name} {leader.last_name}
                </p>
                <p className="text-white/60 text-sm">{leader.role}</p>
                {leader.phone && (
                    <p className="text-white/80 text-sm">{formatPhone(leader.phone)}</p>
                )}
            </div>
        </a>
    );
}

export function Landing() {
    const { isLoaded } = useGoogleMapsLoader();
    const [address, setAddress] = useState('');
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const autocompleteRef = useRef(null);
    const resultRef = useRef(null);

    const lookup = useCallback(async (lat, lng) => {
        setLoading(true);
        setError(null);
        try {
            const res = await axios.get(`${API_URL}/api/public/region-lookup`, {
                params: { lat, lng },
            });
            setResult(res.data);
        } catch (err) {
            setError('Could not determine your region. Please try a different address.');
            setResult(null);
        } finally {
            setLoading(false);
        }
    }, []);

    const onPlaceChanged = useCallback(() => {
        const place = autocompleteRef.current?.getPlace();
        if (place?.geometry) {
            setAddress(place.formatted_address || '');
            lookup(place.geometry.location.lat(), place.geometry.location.lng());
        }
    }, [lookup]);

    // Scroll to result when it appears
    useEffect(() => {
        if (result && resultRef.current) {
            resultRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [result]);

    return (
        <div className="min-h-screen bg-black text-white overflow-x-hidden">
            {/* Header nav */}
            <header className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 py-4">
                <a href="https://revivaltabernacle.org" target="_blank" rel="noopener noreferrer" className="flex items-center">
                    <img src="/rt-logo.png" alt="RT" className="w-8 h-8" style={{ filter: 'brightness(0) invert(1)' }} />
                </a>
                <Link
                    to="/login"
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 backdrop-blur-md border border-white/20 text-white text-sm font-medium hover:bg-white/20 transition-colors"
                >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" /><polyline points="10 17 15 12 10 7" /><line x1="15" y1="12" x2="3" y2="12" />
                    </svg>
                    Leader Portal
                </Link>
            </header>

            {/* Hero section with photo grid background */}
            <section className="relative min-h-screen flex flex-col items-center justify-center px-4">
                <PhotoGrid />
                {/* Dark overlay */}
                <div className="absolute inset-0 bg-black/50" />

                {/* Content */}
                <div className="relative z-10 text-center max-w-2xl mx-auto">
                    <img
                        src="/rt-logo.png"
                        alt="Revival Tabernacle"
                        className="w-44 h-44 mx-auto mb-6"
                        style={{ filter: 'brightness(0) invert(1)' }}
                    />
                    <h1 className="text-4xl sm:text-5xl font-bold font-serif tracking-tight">
                        Revival Tabernacle
                    </h1>
                    <p
                        className="text-4xl sm:text-5xl mt-1 opacity-90"
                        style={{ fontFamily: 'Mistrully, cursive', letterSpacing: '-1px' }}
                    >
                        Care Groups
                    </p>
                    <p className="mt-6 text-lg text-white/70 max-w-md mx-auto leading-relaxed">
                        Find the care group region nearest to you and connect with your regional leaders.
                    </p>

                    {/* Address input */}
                    <div className="mt-8 w-full max-w-lg mx-auto">
                        {isLoaded && window.google?.maps?.places ? (
                            <Autocomplete
                                onLoad={(a) => { autocompleteRef.current = a; }}
                                onPlaceChanged={onPlaceChanged}
                                options={{ componentRestrictions: { country: 'us' } }}
                            >
                                <input
                                    type="text"
                                    value={address}
                                    onChange={(e) => setAddress(e.target.value)}
                                    placeholder="Enter your address..."
                                    className="w-full px-5 py-4 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 text-white text-lg placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-white/40 transition-all"
                                />
                            </Autocomplete>
                        ) : (
                            <input
                                type="text"
                                value={address}
                                onChange={(e) => setAddress(e.target.value)}
                                placeholder="Loading address search..."
                                disabled
                                className="w-full px-5 py-4 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 text-white/40 text-lg cursor-not-allowed"
                            />
                        )}
                        {loading && (
                            <p className="mt-3 text-white/60 animate-pulse">Finding your region...</p>
                        )}
                        {error && (
                            <p className="mt-3 text-red-400 text-sm">{error}</p>
                        )}
                    </div>

                    {/* Scroll hint */}
                    {!result && (
                        <div className="mt-12 animate-bounce text-white/30">
                            <svg className="w-6 h-6 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                            </svg>
                        </div>
                    )}
                </div>
            </section>

            {/* Result section */}
            {result && (
                <section
                    ref={resultRef}
                    className="relative min-h-screen flex items-center justify-center px-4 py-20"
                    style={{ background: `linear-gradient(135deg, ${result.color}33 0%, #000 60%)` }}
                >
                    <div className="max-w-lg w-full space-y-8 animate-fade-in">
                        <div className="text-center">
                            <div
                                className="inline-block w-5 h-5 rounded-full mb-4"
                                style={{ backgroundColor: result.color }}
                            />
                            <h2 className="text-3xl sm:text-4xl font-bold">
                                {result.region} Region
                            </h2>
                        </div>

                        {result.leaders.length > 0 && (
                            <div className="space-y-3">
                                <h3 className="text-sm font-medium text-white/50 uppercase tracking-wider text-center">
                                    Your Regional Leaders
                                </h3>
                                {result.leaders.map((l, i) => (
                                    <LeaderCard key={i} leader={l} />
                                ))}
                            </div>
                        )}

                        <div className="text-center pt-4">
                            <button
                                onClick={() => {
                                    setResult(null);
                                    setAddress('');
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                }}
                                className="px-6 py-3 rounded-xl bg-white/10 backdrop-blur border border-white/20 text-white font-medium hover:bg-white/20 transition-colors"
                            >
                                Search Another Address
                            </button>
                        </div>
                    </div>
                </section>
            )}

            {/* Footer */}
            <footer className="relative border-t border-white/10 py-8 px-4">
                <div className="flex flex-col items-center gap-4">
                    <div className="flex items-center gap-5">
                        <a
                            href="https://www.instagram.com/_revivaltabernacle/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-white/30 hover:text-white/60 transition-colors"
                            aria-label="Instagram"
                        >
                            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                            </svg>
                        </a>
                        <a
                            href="https://www.youtube.com/@joshherringministries4443"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-white/30 hover:text-white/60 transition-colors"
                            aria-label="YouTube"
                        >
                            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                            </svg>
                        </a>
                    </div>
                </div>
            </footer>
        </div>
    );
}

export default Landing;
